// File: frontend/src/pages/legal/LegalPage.tsx

import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import { FaFileContract, FaShieldAlt, FaCookieBite, FaGavel } from "react-icons/fa";
import {
    getLegalDocument,
    LegalDocType,
    LegalDocument,
    LEGAL_ROUTES,
    COOKIE_PREFERENCES_ROUTE,
} from "../../services/legalService";
import "./LegalPage.css";

interface LegalPageProps {
    docType: LegalDocType;
}

// ── Icon per document type ────────────────────────────────────────────────────

const ICONS: Record<LegalDocType, React.ReactNode> = {
    TERMS:   <FaFileContract />,
    PRIVACY: <FaShieldAlt />,
    COOKIE:  <FaCookieBite />,
    // Gavel for the community rules — a nod to the Hammurabi stele the page is named after
    RULES:   <FaGavel />,
};

/**
 * Renders one legal document.
 *
 * The body arrives from the backend with every {{setting:...}} / {{const:...}}
 * token already resolved, so there is no interpolation here. It is inserted with
 * dangerouslySetInnerHTML because the markup is authored exclusively by an ADMIN
 * through the admin panel — it is never user input, and the same approach is
 * already used for the admin manual copy.
 */
export const LegalPage: React.FC<LegalPageProps> = ({ docType }) => {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();

    const [doc, setDoc] = useState<LegalDocument | null>(null);
    const [loading, setLoading] = useState(true);
    const [failed, setFailed] = useState(false);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setFailed(false);

        getLegalDocument(docType, i18n.language)
            .then((result) => {
                if (cancelled) return;
                setDoc(result);
            })
            .catch(() => {
                if (!cancelled) setFailed(true);
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });

        // Reading a legal text always starts at the top, even when the user
        // arrived from a footer link half way down another page
        window.scrollTo(0, 0);

        return () => {
            cancelled = true;
        };
    }, [docType, i18n.language]);

    /**
     * Route same-origin links inside the document body through the router.
     *
     * The body is injected as raw HTML, so an <a href="/hamur-abi-kurallari">
     * written by an admin would otherwise trigger a full page reload and
     * restart the whole SPA. External links and anything opened in a new tab
     * are left to the browser.
     */
    const handleBodyClick = useCallback((event: React.MouseEvent<HTMLElement>) => {
        const anchor = (event.target as HTMLElement).closest("a");
        if (!anchor) return;

        const href = anchor.getAttribute("href") ?? "";
        // Only plain in-app paths: not "//host", not "http(s)://", not "#anchor"
        if (!href.startsWith("/") || href.startsWith("//")) return;
        if (anchor.target && anchor.target !== "_self") return;
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;

        event.preventDefault();
        navigate(href);
    }, [navigate]);

    // Format the effective date in the reader's locale; the API sends ISO
    const formatDate = (iso: string | null): string => {
        if (!iso) return "";
        const parsed = new Date(iso);
        if (Number.isNaN(parsed.getTime())) return "";
        return parsed.toLocaleDateString(i18n.language === "tr" ? "tr-TR" : "en-US", {
            day: "numeric",
            month: "long",
            year: "numeric",
        });
    };

    if (loading) {
        return (
            <div className="legal-page">
                <div className="legal-state">{t("loading", "Yükleniyor...")}</div>
            </div>
        );
    }

    if (failed || !doc) {
        return (
            <div className="legal-page">
                <div className="legal-state legal-state--error">
                    {t("legal_not_available", "Bu metin şu anda görüntülenemiyor.")}
                </div>
            </div>
        );
    }

    return (
        <div className="legal-page">
            <div className="legal-header">
                <span className="legal-header-icon">{ICONS[docType]}</span>
                <div className="legal-header-text">
                    <h1 className="legal-title">{doc.title}</h1>
                    <div className="legal-meta">
                        <span className="legal-version">
                            {t("legal_version", "Sürüm")} {doc.version}
                        </span>
                        {doc.effectiveDate && (
                            <>
                                <span className="legal-meta-sep">·</span>
                                <span className="legal-effective">
                                    {t("legal_effective_date", "Yürürlük")}: {formatDate(doc.effectiveDate)}
                                </span>
                            </>
                        )}
                    </div>
                </div>
            </div>

            {/* Admin-authored markup — see the component comment above */}
            <article
                className="legal-body"
                onClick={handleBodyClick}
                dangerouslySetInnerHTML={{ __html: doc.body }}
            />

            {/* Cross-links so a reader can move between the three documents
                without going back to the footer */}
            <nav className="legal-crosslinks">
                {docType !== "RULES" && (
                    <Link to={LEGAL_ROUTES.RULES} className="legal-crosslink">
                        {t("legal_link_rules", "Hamur Abi Kuralları")}
                    </Link>
                )}
                {docType !== "TERMS" && (
                    <Link to={LEGAL_ROUTES.TERMS} className="legal-crosslink">
                        {t("legal_link_terms", "Kullanım Koşulları")}
                    </Link>
                )}
                {docType !== "PRIVACY" && (
                    <Link to={LEGAL_ROUTES.PRIVACY} className="legal-crosslink">
                        {t("legal_link_privacy", "Gizlilik Politikası")}
                    </Link>
                )}
                {docType !== "COOKIE" && (
                    <Link to={LEGAL_ROUTES.COOKIE} className="legal-crosslink">
                        {t("legal_link_cookie", "Çerez Politikası")}
                    </Link>
                )}
                <Link to={COOKIE_PREFERENCES_ROUTE} className="legal-crosslink">
                    {t("legal_link_cookie_prefs", "Çerez Tercihleri")}
                </Link>
            </nav>
        </div>
    );
};